import { ArrowLeft, History } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { DateInput, Field, FormSection, SelectInput } from '@/components/Admin/AdminFormFields';
import { AutosaveTextInput } from '@/components/Admin/Autosave/AutosaveTextInput';
import { GuideSpecialtiesField } from '@/components/Admin/GuideSpecialtiesField';
import { HeroImageField } from '@/components/Admin/HeroImageField';
import { SlugField } from '@/components/Admin/SlugField';
import { Button } from '@/components/ui/button';
import { createGuide, getGuide, isGuideIdTaken } from '@/data/guidesRepo';
import { DocumentIdTakenError } from '@/data/util_documentIds';
import { HistoryPanel } from '@/hooks/autosave/HistoryPanel';
import { TopWhiteBarEditControls } from '@/hooks/autosave/TopWhiteBarEditControls';
import { UndoRedoButtons } from '@/hooks/autosave/UndoRedoButtons';
import { useAutosaveGuide } from '@/hooks/autosave/useAutosaveGuide';
import { undoRedoStorageKey, useUndoRedoStack } from '@/hooks/autosave/useUndoRedoStack';
import { countFormatter, describeChange, labelFormatter, type ValueFormatterType } from '@/hooks/autosave/util_historyMessage';
import { useSaveStack } from '@/hooks/autosave/util_saveStack';
import {
  coll_guidesHistory,
  guideStatusLabels,
  makeEmptyGuide,
  parseGuideLanguages,
  type GuideSpecialtyType,
  type GuideStatusType,
  type GuideType,
} from '@/types/types_guide';
import { formatCalendarDate, todayCalendarDate } from '@/util/util_format';
import { slugify, validateSlugForCreate } from '@/util/util_slug';

interface PageGuideEditProps {
  mode: 'new' | 'edit';
}

/**
 * Create / edit form for a single tour leader.
 *
 * Same two-mode shape as the partner form: in `edit` mode every field autosaves
 * on commit (blur), with a sticky top bar, local undo/redo and persisted
 * history; in `new` mode there is a Create button, since there is nothing to
 * autosave until the document exists.
 */
export function PageGuideEdit({ mode }: PageGuideEditProps) {
  const { guideId } = useParams<{ guideId: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<GuideType>(() => makeEmptyGuide());
  const formRef = useRef(form);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // Why the Add button refused, shown above it, and the id field's own
  // complaint. Both `new` mode only: an edit saves per field and reports a
  // problem on the field itself, and an existing guide's id is fixed.
  const [createError, setCreateError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);

  const saveStack = useSaveStack();
  const autosave = useAutosaveGuide(mode === 'edit' ? guideId : undefined, saveStack);
  const undoRedo = useUndoRedoStack();
  const { loadFor: loadUndoRedoFor } = undoRedo;

  useEffect(() => {
    if (mode !== 'edit' || !guideId) return;
    let cancelled = false;
    getGuide(guideId)
      .then((guide) => {
        if (cancelled) return;
        if (guide) {
          formRef.current = guide;
          setForm(guide);
          loadUndoRedoFor(
            undoRedoStorageKey('guide', guideId),
            (field) => formRef.current[field as keyof GuideType],
          );
        } else {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, guideId, loadUndoRedoFor]);

  // Write a field value: reflect locally and, in edit mode, autosave it along
  // with a persisted history step. Shared by direct edits and by undo/redo.
  function applyChange(field: string, value: unknown, userAction: string, isUndoStep = false) {
    if (mode === 'edit') {
      const { newHistory } = autosave(userAction, { [field]: value }, formRef.current.history, isUndoStep);
      formRef.current = { ...formRef.current, [field]: value, history: newHistory };
      setForm(formRef.current);
    } else {
      formRef.current = { ...formRef.current, [field]: value };
      setForm(formRef.current);
    }
  }

  // A user edit: apply it and (edit mode) record it on the undo stack.
  function commit<K extends keyof GuideType>(
    key: K,
    value: GuideType[K],
    label: string,
    format?: ValueFormatterType,
  ) {
    const prevValue = formRef.current[key];
    if (Object.is(prevValue, value)) return;
    const describe = (v: unknown) => describeChange(label, v, format);
    applyChange(key, value, describe(value));
    if (mode === 'edit') {
      undoRedo.record({
        field: key,
        prevValue,
        nextValue: value,
        undoMessage: describe(prevValue),
        redoMessage: describe(value),
      });
    }
  }

  // The id, written straight onto the form. Stable so that SlugField's effect —
  // which fills the field in from the name — does not re-run per render.
  const setSlug = useCallback((slug: string) => {
    formRef.current = { ...formRef.current, id: slug };
    setForm(formRef.current);
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setCreateError(null);
    // Checked again here, not only on blur: the field validates what was in it
    // when the user left it, and the roster may have moved on since. Two tour
    // leaders of the same name are likelier here than anywhere else.
    const id = slugify(formRef.current.id || formRef.current.name);
    const idError = await validateSlugForCreate(id, isGuideIdTaken, 'tour leader');
    if (idError) {
      setSlugError(idError);
      setCreateError('Check the highlighted field above.');
      setSaving(false);
      return;
    }
    const toSave: GuideType = { ...formRef.current, id };
    try {
      await createGuide(toSave);
    } catch (e: unknown) {
      // The id was free a moment ago and is not any more — somebody else created
      // it in between, and the transaction refused rather than writing over them.
      if (e instanceof DocumentIdTakenError) {
        setSlugError('That id was taken while this form was open. Choose a different one.');
        setCreateError('Check the highlighted field above.');
      } else {
        console.error('Create guide failed', e);
        setCreateError('The tour leader could not be created. Try again.');
      }
      setSaving(false);
      return;
    }
    setSaving(false);
    navigate('/admin/guides');
  }

  if (loading) {
    return <div className='tw:py-16 tw:text-center tw:text-muted-foreground'>Loading…</div>;
  }

  if (notFound) {
    return (
      <div className='tw:py-16 tw:text-center'>
        <p className='tw:text-lg'>Tour leader not found.</p>
        <Link to='/admin/guides' className='tw:mt-4 tw:inline-block tw:text-primary tw:underline'>
          Back to tour leaders
        </Link>
      </div>
    );
  }

  const isEdit = mode === 'edit';

  const fields = (
    <div className='tw:space-y-8'>
      {/* Profile */}
      <FormSection title='Profile'>
        <Field label='Name' required>
          <AutosaveTextInput currentValue={form.name} onCommitChange={(v) => commit('name', v, 'name')} />
        </Field>
        {!isEdit && (
          <SlugField
            label='Record ID'
            name={form.name}
            value={form.id}
            onChange={setSlug}
            isTaken={isGuideIdTaken}
            entityLabel='tour leader'
            error={slugError}
            onErrorChange={setSlugError}
          />
        )}
        <Field label='Name (Japanese)'>
          <AutosaveTextInput
            currentValue={form.nameJp}
            placeholder='田中 太郎'
            onCommitChange={(v) => commit('nameJp', v, 'Japanese name')}
          />
        </Field>
        <Field label='Status'>
          <SelectInput
            value={form.status}
            options={guideStatusLabels}
            onValueChange={(v) =>
              commit('status', v as GuideStatusType, 'status', labelFormatter(guideStatusLabels))
            }
          />
        </Field>
        <Field label='Display Order'>
          <AutosaveTextInput
            numeric
            currentValue={form.displayOrder.toString()}
            onCommitChange={(v) => commit('displayOrder', Number(v) || 0, 'display order')}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            Lowest first on the public Tour Leaders page. Tour leaders sharing a number are listed by name.
          </p>
        </Field>
        <Field label='Photo' className='tw:sm:col-span-2'>
          <HeroImageField
            folder='guides'
            value={form.photo}
            onChange={(url) => commit('photo', url, 'photo', () => null)}
          />
        </Field>
        <Field label='Biography' className='tw:sm:col-span-2'>
          <AutosaveTextInput
            multiline
            rows={5}
            currentValue={form.bio}
            placeholder='How this tour leader is introduced to a guest.'
            onCommitChange={(v) => commit('bio', v, 'biography')}
          />
        </Field>
      </FormSection>

      {/* Expertise */}
      <FormSection title='Expertise'>
        <Field label='Specialties' className='tw:sm:col-span-2'>
          <GuideSpecialtiesField
            value={form.specialties}
            onChange={(v: GuideSpecialtyType[]) =>
              commit('specialties', v, 'specialties', countFormatter('specialty'))
            }
          />
        </Field>
        <Field label='Languages'>
          <AutosaveTextInput
            currentValue={form.languages.join(', ')}
            placeholder='English, Japanese'
            onCommitChange={(v) => {
              const next = parseGuideLanguages(v);
              // `commit` bails on `Object.is`, which two equal arrays never
              // satisfy — so an unchanged list is compared by value here, or
              // every blur would write a history step saying nothing happened.
              if (next.join(' ') === form.languages.join(' ')) return;
              commit('languages', next, 'languages', (value) =>
                Array.isArray(value) ? value.join(', ') : undefined,
              );
            }}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>Comma-separated.</p>
        </Field>
        <Field label='Guiding Since'>
          <DateInput
            value={form.guidingSince}
            max={todayCalendarDate()}
            onChange={(v) =>
              commit('guidingSince', v, 'guiding since date', (value) =>
                typeof value === 'string' ? formatCalendarDate(value) : undefined,
              )
            }
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            When this tour leader started guiding. The years of experience shown to guests are counted from
            here, so they stay right without anyone editing them.
          </p>
        </Field>
      </FormSection>

      {/* Portal link */}
      <FormSection title='Portal Calendar'>
        <Field label='Portal Calendar ID' className='tw:sm:col-span-2'>
          <AutosaveTextInput
            currentValue={form.portalGuideId}
            placeholder='Their user id in the Eighty Days portal'
            onCommitChange={(v) => commit('portalGuideId', v.trim(), 'portal calendar ID')}
          />
          <p className='tw:mt-1 tw:text-xs tw:text-muted-foreground'>
            How this tour leader is matched to their diary in the portal — the id of their user record there,
            not their email address. Leave it blank and the public page shows the profile but says availability
            is on request.
          </p>
        </Field>
      </FormSection>
    </div>
  );

  // ----- Edit mode: sticky autosave bar, no submit button -----
  if (isEdit) {
    return (
      <div className='tw:mx-auto tw:max-w-6xl'>
        <TopWhiteBarEditControls
          saveStack={saveStack}
          leftSlot={
            <Link
              to='/admin/guides'
              className='tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground'
            >
              <ArrowLeft className='tw:size-4' /> Tour Leaders
            </Link>
          }
          middleSlot={
            <UndoRedoButtons
              canUndo={undoRedo.canUndo}
              canRedo={undoRedo.canRedo}
              undoLabel={undoRedo.undoLabel}
              redoLabel={undoRedo.redoLabel}
              onUndo={() => undoRedo.undo((f, v, a) => applyChange(f, v, a, true))}
              onRedo={() => undoRedo.redo((f, v, a) => applyChange(f, v, a, true))}
            />
          }
        >
          <button
            type='button'
            onClick={() => setShowHistory(true)}
            className='tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-ed-stone tw:transition-colors tw:hover:text-ed-gold'
          >
            <History className='tw:size-4' /> History
            {!!form.history?.lastStep && <span className='tw:text-ed-stone/70'>({form.history.lastStep})</span>}
          </button>
        </TopWhiteBarEditControls>
        <header className='tw:mb-8'>
          <h1 className='tw:font-serif tw:text-3xl tw:font-light'>Edit — {form.name}</h1>
          <p className='tw:mt-1 tw:text-sm tw:text-muted-foreground'>Changes save automatically.</p>
        </header>
        {fields}
        <HistoryPanel
          key={guideId}
          show={showHistory}
          onClose={() => setShowHistory(false)}
          history={form.history}
          historyCollectionName={coll_guidesHistory}
        />
      </div>
    );
  }

  // ----- New mode: create-then-navigate, no autosave -----
  return (
    <form onSubmit={handleCreate} className='tw:mx-auto tw:max-w-6xl'>
      <header className='tw:mb-8'>
        <Link
          to='/admin/guides'
          className='tw:inline-flex tw:items-center tw:gap-1 tw:text-sm tw:text-muted-foreground tw:hover:text-foreground'
        >
          <ArrowLeft className='tw:size-4' /> Tour Leaders
        </Link>
        <h1 className='tw:mt-2 tw:font-serif tw:text-3xl tw:font-light'>Add Tour Leader</h1>
      </header>

      {fields}

      {createError && (
        <p className='tw:mt-6 tw:text-right tw:text-sm tw:text-destructive'>{createError}</p>
      )}

      <div className='tw:mt-8 tw:flex tw:justify-end tw:gap-3'>
        <Button asChild variant='outline' type='button'>
          <Link to='/admin/guides'>Cancel</Link>
        </Button>
        <Button type='submit' disabled={!form.name.trim()} loading={saving}>
          {saving ? 'Saving…' : 'Add Tour Leader'}
        </Button>
      </div>
    </form>
  );
}
